import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { useLanguage } from '../context/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Plus, 
  ChartBar,
  Calendar,
  Truck,
  Pencil,
  FilePdf,
  Upload,
  Check,
  X,
  Knife,
  Warehouse,
} from '@phosphor-icons/react';
import { Trash2, Beef, ChefHat, FileUp, AlertCircle, Scale, Sparkles } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';

const categoryLabels = {
  tavuk: { label: 'Tavuk', color: 'bg-yellow-100 text-yellow-800' },
  dana: { label: 'Dana', color: 'bg-red-100 text-red-800' },
  kuzu: { label: 'Kuzu', color: 'bg-orange-100 text-orange-800' },
  balik: { label: 'Balık', color: 'bg-blue-100 text-blue-800' },
  sakatat: { label: 'Sakatat', color: 'bg-purple-100 text-purple-800' },
};

const productTypeLabels = {
  karkas: 'Karkas',
  parcali: 'Parçalı',
  islenmiş: 'İşlenmiş',
};

export default function MeatManagement() {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const [entries, setEntries] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  
  // Tarih filtreleri
  const [filterMode, setFilterMode] = useState('month');
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  const [suggestions, setSuggestions] = useState({ products: [], suppliers: [] });
  const [activeTab, setActiveTab] = useState('list');
  
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    product_name: '',
    category: 'tavuk',
    product_type: 'karkas',
    quantity: '',
    unit: 'kg',
    unit_price: '',
    supplier: '',
    invoice_no: '',
    notes: '',
    send_to_processing: false,
    add_to_stock: false,
  });
  
  // PDF Import
  const [isPdfDialogOpen, setIsPdfDialogOpen] = useState(false);
  const [pdfFile, setPdfFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfDate, setPdfDate] = useState(new Date().toISOString().split('T')[0]);
  const [pdfSupplier, setPdfSupplier] = useState('');
  const [pdfSendToProcessing, setPdfSendToProcessing] = useState(false);
  const [pdfAddToStock, setPdfAddToStock] = useState(false);

  // Karkas İşleme
  const [isProcessingDialogOpen, setIsProcessingDialogOpen] = useState(false);
  const [processingEntry, setProcessingEntry] = useState(null);
  const [processingOutputs, setProcessingOutputs] = useState([{ name: '', quantity: '' }]);
  const [wasteQuantity, setWasteQuantity] = useState('');
  const [processingQueue, setProcessingQueue] = useState([]);

  const isAdmin = user?.role === 'admin';
  const isManager = user?.role === 'admin' || user?.role === 'manager';

  useEffect(() => {
    fetchData();
    fetchSuggestions();
    fetchProcessingQueue();
  }, [selectedMonth, startDate, endDate, filterMode]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '/meat';
      let reportUrl = '/meat/report';
      
      if (filterMode === 'range' && startDate && endDate) {
        url += `?start_date=${startDate}&end_date=${endDate}`;
        reportUrl += `?start_date=${startDate}&end_date=${endDate}`;
      } else {
        url += `?month=${selectedMonth}`;
        reportUrl += `?month=${selectedMonth}`;
      }
      
      const [entriesRes, reportRes] = await Promise.all([
        api.get(url),
        api.get(reportUrl)
      ]);
      setEntries(entriesRes.data);
      setReport(reportRes.data);
    } catch (error) {
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const fetchProcessingQueue = async () => {
    try {
      const res = await api.get('/processing/queue');
      setProcessingQueue(res.data);
    } catch (error) {
      console.error('Processing queue fetch error:', error);
    }
  };

  const fetchSuggestions = async () => {
    try {
      const res = await api.get('/meat/suggestions');
      setSuggestions(res.data);
    } catch (error) {
      console.error('Suggestions fetch error:', error);
    }
  };

  const resetForm = () => {
    setEditingEntry(null);
    setFormData({
      date: new Date().toISOString().split('T')[0],
      product_name: '',
      category: 'tavuk',
      product_type: 'karkas',
      quantity: '',
      unit: 'kg',
      unit_price: '',
      supplier: '',
      invoice_no: '',
      notes: '',
      send_to_processing: false,
      add_to_stock: false,
    });
  };

  const handleSubmit = async () => {
    if (!formData.product_name || !formData.quantity || !formData.unit_price) {
      toast.error('Ürün adı, miktar ve birim fiyat gerekli');
      return;
    }

    try {
      const payload = {
        ...formData,
        quantity: parseFloat(formData.quantity),
        unit_price: parseFloat(formData.unit_price),
        total_amount: parseFloat(formData.quantity) * parseFloat(formData.unit_price),
      };

      if (editingEntry) {
        await api.put(`/meat/${editingEntry.id}`, payload);
        toast.success('Kayıt güncellendi');
      } else {
        await api.post('/meat', payload);
        let msg = 'Kayıt eklendi';
        if (formData.send_to_processing) msg += ' ve Hazırlık Mutfağına gönderildi';
        if (formData.add_to_stock) msg += ' ve depo stoğuna eklendi';
        toast.success(msg);
      }

      setIsDialogOpen(false);
      resetForm();
      fetchData();
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşlem başarısız');
    }
  };

  const handleEdit = (entry) => {
    setEditingEntry(entry);
    setFormData({
      date: entry.date,
      product_name: entry.product_name,
      category: entry.category,
      product_type: entry.product_type,
      quantity: entry.quantity.toString(),
      unit: entry.unit,
      unit_price: entry.unit_price.toString(),
      supplier: entry.supplier || '',
      invoice_no: entry.invoice_no || '',
      notes: entry.notes || '',
      send_to_processing: false,
      add_to_stock: false,
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm('Bu kaydı silmek istediğinize emin misiniz?')) return;
    
    try {
      await api.delete(`/meat/${id}`);
      toast.success('Kayıt silindi');
      fetchData();
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  // PDF Import
  const handlePdfFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Sadece PDF dosyaları kabul edilir');
      return;
    }
    
    setPdfFile(file);
    setPdfPreview(null);
    setPdfLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post('/meat/preview-pdf', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setPdfPreview(res.data);
      if (res.data.summary?.detected_supplier) {
        setPdfSupplier(res.data.summary.detected_supplier);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF okunamadı');
      setPdfFile(null);
    } finally {
      setPdfLoading(false);
    }
  };

  const handlePdfImport = async () => {
    if (!pdfFile) return;
    
    setPdfLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', pdfFile);
      
      const params = new URLSearchParams();
      params.append('date', pdfDate);
      if (pdfSupplier) params.append('supplier', pdfSupplier);
      params.append('send_to_processing', pdfSendToProcessing.toString());
      params.append('add_to_stock', pdfAddToStock.toString());
      params.append('use_ai', 'true');
      
      const res = await api.post(`/meat/import-pdf?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      const aiMsg = res.data.ai_used ? ' (AI)' : '';
      const stockMsg = pdfAddToStock ? ' ve depo stoğuna eklendi' : '';
      toast.success(`${res.data.summary.entry_count} ürün aktarıldı${aiMsg}${stockMsg}! Toplam: ₺${res.data.summary.total_amount.toLocaleString('tr-TR')}`);
      setIsPdfDialogOpen(false);
      resetPdfDialog();
      fetchData();
      fetchSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'PDF aktarımı başarısız');
    } finally {
      setPdfLoading(false);
    }
  };

  const resetPdfDialog = () => {
    setPdfFile(null);
    setPdfPreview(null);
    setPdfSupplier('');
    setPdfDate(new Date().toISOString().split('T')[0]);
    setPdfSendToProcessing(false);
    setPdfAddToStock(false);
  };

  // Karkas İşleme
  const openProcessingDialog = (entry) => {
    setProcessingEntry(entry);
    setProcessingOutputs([{ name: '', quantity: '' }]);
    setWasteQuantity('');
    setIsProcessingDialogOpen(true);
  };

  const addOutputRow = () => {
    setProcessingOutputs([...processingOutputs, { name: '', quantity: '' }]);
  };

  const removeOutputRow = (index) => {
    setProcessingOutputs(processingOutputs.filter((_, i) => i !== index));
  };

  const updateOutputRow = (index, field, value) => {
    const updated = [...processingOutputs];
    updated[index][field] = value;
    setProcessingOutputs(updated);
  };

  const handleProcessCarcass = async () => {
    if (!processingEntry) return;
    
    const validOutputs = processingOutputs.filter(o => o.name && o.quantity);
    if (validOutputs.length === 0) {
      toast.error('En az bir çıktı parçası girin');
      return;
    }

    try {
      const payload = {
        meat_entry_id: processingEntry.id,
        date: new Date().toISOString().split('T')[0],
        outputs: validOutputs.map(o => ({
          name: o.name,
          quantity: parseFloat(o.quantity),
          unit: 'kg'
        })),
        waste_quantity: parseFloat(wasteQuantity) || 0,
      };

      const res = await api.post('/processing/carcass', payload);
      toast.success(res.data.message);
      setIsProcessingDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İşleme başarısız');
    }
  };

  const formatCurrency = (value) => {
    return `₺${new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2 }).format(value || 0)}`;
  };

  const getCategoryBadge = (category) => {
    const cat = categoryLabels[category] || { label: category, color: 'bg-gray-100 text-gray-800' };
    return <Badge className={cat.color}>{cat.label}</Badge>;
  };

  const getMonthName = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('tr-TR', { month: 'long', year: 'numeric' });
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="meat-management">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <Beef className="w-8 h-8 text-red-600" />
              {t('meat.title')}
            </h1>
            <p className="text-muted-foreground">{t('meat.subtitle')}</p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {/* Filtre Modu */}
            <div className="flex items-center gap-2 border rounded-lg p-1">
              <Button
                variant={filterMode === 'month' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('month')}
              >
                {t('common.month')}
              </Button>
              <Button
                variant={filterMode === 'range' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilterMode('range')}
              >
                {t('common.dateRange')}
              </Button>
            </div>
            
            {filterMode === 'month' ? (
              <Input
                type="month"
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(e.target.value)}
                className="w-40"
              />
            ) : (
              <div className="flex items-center gap-2">
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-36"
                />
                <span className="text-muted-foreground">-</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-36"
                />
              </div>
            )}
            
            {isManager && (
              <>
                {/* PDF'den Aktar */}
                <Dialog open={isPdfDialogOpen} onOpenChange={(open) => { setIsPdfDialogOpen(open); if (!open) resetPdfDialog(); }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="border-red-300 text-red-600 hover:bg-red-50">
                      <FilePdf className="w-4 h-4 mr-2" />
                      PDF'den Aktar
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle className="flex items-center gap-2">
                        <FilePdf className="w-5 h-5 text-red-600" />
                        Et/Tavuk PDF Fatura Aktarımı
                      </DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      {/* Dosya Seçimi */}
                      <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          accept=".pdf"
                          onChange={handlePdfFileSelect}
                          className="hidden"
                          id="meat-pdf-upload"
                        />
                        <label htmlFor="meat-pdf-upload" className="cursor-pointer">
                          {pdfFile ? (
                            <div className="flex items-center justify-center gap-3">
                              <FilePdf className="w-10 h-10 text-red-500" />
                              <div className="text-left">
                                <p className="font-medium">{pdfFile.name}</p>
                                <p className="text-sm text-muted-foreground">{(pdfFile.size / 1024).toFixed(1)} KB</p>
                              </div>
                              <Button type="button" variant="ghost" size="sm" onClick={(e) => { e.preventDefault(); setPdfFile(null); setPdfPreview(null); }}>
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ) : (
                            <div>
                              <FileUp className="w-12 h-12 text-slate-400 mx-auto mb-2" />
                              <p className="font-medium">Et/Tavuk faturasını seçin</p>
                            </div>
                          )}
                        </label>
                      </div>
                      
                      {/* Tarih, Tedarikçi, Hazırlık Mutfağı */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Fatura Tarihi</Label>
                          <Input type="date" value={pdfDate} onChange={(e) => setPdfDate(e.target.value)} />
                        </div>
                        <div>
                          <Label>Tedarikçi</Label>
                          <Input value={pdfSupplier} onChange={(e) => setPdfSupplier(e.target.value)} placeholder="Otomatik algılanır" />
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                        <Checkbox
                          id="pdf-send-processing"
                          checked={pdfSendToProcessing}
                          onCheckedChange={setPdfSendToProcessing}
                        />
                        <label htmlFor="pdf-send-processing" className="text-sm text-amber-700 cursor-pointer">
                          <ChefHat className="w-4 h-4 inline mr-1" />
                          Karkas ürünleri Hazırlık Mutfağına gönder
                        </label>
                      </div>
                      
                      {/* Depo Stoğuna Ekle */}
                      <div className="flex items-center space-x-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                        <Checkbox
                          id="pdf-add-to-stock-meat"
                          checked={pdfAddToStock}
                          onCheckedChange={(checked) => setPdfAddToStock(checked)}
                          data-testid="pdf-add-to-stock"
                        />
                        <div className="flex items-center gap-2">
                          <Warehouse className="w-5 h-5 text-emerald-600" />
                          <Label htmlFor="pdf-add-to-stock-meat" className="cursor-pointer font-medium text-emerald-700">
                            Depo Stoğuna Ekle & Fiyat Güncelle
                          </Label>
                        </div>
                      </div>
                      
                      {pdfPreview && (
                        <div className="flex items-center gap-2 text-sm text-red-600">
                          <Sparkles className="w-4 h-4" />
                          <span>AI destekli akıllı fatura okuma aktif</span>
                        </div>
                      )}
                      
                      {pdfLoading && (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mr-3"></div>
                          <span>PDF okunuyor...</span>
                        </div>
                      )}
                      
                      {pdfPreview && !pdfLoading && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between bg-red-50 p-3 rounded-lg border border-red-200">
                            <div className="flex items-center gap-2">
                              <Check className="w-5 h-5 text-red-600" />
                              <span className="font-medium text-red-700">{pdfPreview.summary.entry_count} ürün bulundu</span>
                            </div>
                            <span className="text-lg font-bold text-red-700">{formatCurrency(pdfPreview.summary.total_amount)}</span>
                          </div>
                          
                          <div className="max-h-64 overflow-y-auto border rounded-lg">
                            <Table>
                              <TableHeader>
                                <TableRow>
                                  <TableHead>Ürün</TableHead>
                                  <TableHead>Kategori</TableHead>
                                  <TableHead className="text-right">Miktar</TableHead>
                                  <TableHead className="text-right">Birim Fiyat</TableHead>
                                  <TableHead className="text-right">Toplam</TableHead>
                                </TableRow>
                              </TableHeader>
                              <TableBody>
                                {pdfPreview.items.map((item, idx) => (
                                  <TableRow key={idx}>
                                    <TableCell className="font-medium">{item.product_name}</TableCell>
                                    <TableCell>{getCategoryBadge(item.category)}</TableCell>
                                    <TableCell className="text-right">{item.quantity} {item.unit}</TableCell>
                                    <TableCell className="text-right">{formatCurrency(item.unit_price)}</TableCell>
                                    <TableCell className="text-right font-bold">{formatCurrency(item.total_amount)}</TableCell>
                                  </TableRow>
                                ))}
                              </TableBody>
                            </Table>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex gap-3 pt-2">
                        <Button variant="outline" className="flex-1" onClick={() => { setIsPdfDialogOpen(false); resetPdfDialog(); }}>İptal</Button>
                        <Button onClick={handlePdfImport} disabled={!pdfPreview || pdfLoading} className="flex-1 bg-red-600 hover:bg-red-700">
                          <Upload className="w-4 h-4 mr-2" />
                          {pdfPreview?.summary?.entry_count || 0} Ürünü Kaydet
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
                
                {/* Manuel Ekleme */}
                <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
                  <DialogTrigger asChild>
                    <Button className="bg-red-600 hover:bg-red-700">
                      <Plus className="w-4 h-4 mr-2" />
                      Et/Tavuk Ekle
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>{editingEntry ? 'Et/Tavuk Düzenle' : 'Yeni Et/Tavuk Girişi'}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto pr-2">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Tarih *</Label>
                          <Input type="date" value={formData.date} onChange={(e) => setFormData({ ...formData, date: e.target.value })} />
                        </div>
                        <div>
                          <Label>Fatura No</Label>
                          <Input value={formData.invoice_no} onChange={(e) => setFormData({ ...formData, invoice_no: e.target.value })} placeholder="Opsiyonel" />
                        </div>
                      </div>
                      
                      <div>
                        <Label>Ürün Adı *</Label>
                        <Input
                          value={formData.product_name}
                          onChange={(e) => setFormData({ ...formData, product_name: e.target.value.toUpperCase() })}
                          placeholder="TAVUK KARKAS, DANA KIYMA vb."
                          list="meat-products"
                        />
                        <datalist id="meat-products">
                          {suggestions.products.map((p, i) => (
                            <option key={i} value={p.name} />
                          ))}
                        </datalist>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label>Kategori *</Label>
                          <Select value={formData.category} onValueChange={(v) => setFormData({ ...formData, category: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="tavuk">Tavuk</SelectItem>
                              <SelectItem value="dana">Dana</SelectItem>
                              <SelectItem value="kuzu">Kuzu</SelectItem>
                              <SelectItem value="balik">Balık</SelectItem>
                              <SelectItem value="sakatat">Sakatat</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Ürün Tipi *</Label>
                          <Select value={formData.product_type} onValueChange={(v) => setFormData({ ...formData, product_type: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="karkas">Karkas (Bütün)</SelectItem>
                              <SelectItem value="parcali">Parçalı</SelectItem>
                              <SelectItem value="islenmiş">İşlenmiş (Kıyma vb.)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <Label>Miktar *</Label>
                          <Input type="number" step="0.01" value={formData.quantity} onChange={(e) => setFormData({ ...formData, quantity: e.target.value })} placeholder="10" />
                        </div>
                        <div>
                          <Label>Birim</Label>
                          <Select value={formData.unit} onValueChange={(v) => setFormData({ ...formData, unit: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="kg">Kg</SelectItem>
                              <SelectItem value="adet">Adet</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label>Birim Fiyat (₺) *</Label>
                          <Input type="number" step="0.01" value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })} placeholder="150" />
                        </div>
                      </div>
                      
                      {formData.quantity && formData.unit_price && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-center">
                          <span className="text-sm text-red-700">Toplam:</span>
                          <span className="text-xl font-bold text-red-700 ml-2">
                            {formatCurrency(parseFloat(formData.quantity) * parseFloat(formData.unit_price))}
                          </span>
                        </div>
                      )}
                      
                      <div>
                        <Label>Tedarikçi</Label>
                        <Input value={formData.supplier} onChange={(e) => setFormData({ ...formData, supplier: e.target.value })} placeholder="Tedarikçi adı" list="meat-suppliers" />
                        <datalist id="meat-suppliers">
                          {suggestions.suppliers.map((s, i) => (
                            <option key={i} value={s} />
                          ))}
                        </datalist>
                      </div>
                      
                      {!editingEntry && formData.product_type === 'karkas' && (
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                          <Checkbox
                            id="send-processing"
                            checked={formData.send_to_processing}
                            onCheckedChange={(checked) => setFormData({ ...formData, send_to_processing: checked })}
                          />
                          <label htmlFor="send-processing" className="text-sm text-amber-700 cursor-pointer">
                            <ChefHat className="w-4 h-4 inline mr-1" />
                            Hazırlık Mutfağına gönder (Karkas işleme için)
                          </label>
                        </div>
                      )}
                      
                      {/* Depo Stoğuna Ekle - Sadece yeni kayıtta */}
                      {!editingEntry && (
                        <div className="flex items-center space-x-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                          <Checkbox
                            id="form-add-to-stock-meat"
                            checked={formData.add_to_stock}
                            onCheckedChange={(checked) => setFormData({ ...formData, add_to_stock: checked })}
                            data-testid="form-add-to-stock"
                          />
                          <div className="flex items-center gap-2">
                            <Warehouse className="w-5 h-5 text-emerald-600" />
                            <Label htmlFor="form-add-to-stock-meat" className="cursor-pointer font-medium text-emerald-700">
                              Depo Stoğuna Ekle & Fiyat Güncelle
                            </Label>
                          </div>
                        </div>
                      )}
                      
                      <Button onClick={handleSubmit} className="w-full bg-red-600 hover:bg-red-700">
                        {editingEntry ? 'Güncelle' : 'Kaydet'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </>
            )}
          </div>
        </div>

        {/* Özet Kartı */}
        {report && (
          <Card className="border-2 border-red-200 bg-red-50">
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-red-700 font-medium uppercase">Toplam Harcama</p>
                  <p className="text-3xl font-bold text-red-700">{formatCurrency(report.total_amount)}</p>
                  <p className="text-xs text-red-600">{report.total_entries} kayıt, {report.total_quantity.toFixed(1)} kg</p>
                </div>
                <div className="p-3 bg-red-200 rounded-full">
                  <Beef className="w-8 h-8 text-red-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tab'lar */}
        <div className="flex gap-2 border-b">
          <Button variant={activeTab === 'list' ? 'default' : 'ghost'} onClick={() => setActiveTab('list')} className="rounded-b-none">
            <Calendar className="w-4 h-4 mr-2" />
            Giriş Listesi
          </Button>
          <Button variant={activeTab === 'processing' ? 'default' : 'ghost'} onClick={() => setActiveTab('processing')} className="rounded-b-none">
            <ChefHat className="w-4 h-4 mr-2" />
            Hazırlık Mutfağı ({processingQueue.filter(p => p.status === 'pending').length})
          </Button>
          <Button variant={activeTab === 'report' ? 'default' : 'ghost'} onClick={() => setActiveTab('report')} className="rounded-b-none">
            <ChartBar className="w-4 h-4 mr-2" />
            Raporlar
          </Button>
        </div>

        {/* Giriş Listesi */}
        {activeTab === 'list' && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">{getMonthName(selectedMonth)} Et/Tavuk Girişleri ({entries.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-600"></div>
                </div>
              ) : entries.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Beef size={48} className="mx-auto mb-3 opacity-50" />
                  <p>Bu ayda et/tavuk girişi yok</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Ürün</TableHead>
                        <TableHead>Kategori</TableHead>
                        <TableHead>Tip</TableHead>
                        <TableHead className="text-right">Miktar</TableHead>
                        <TableHead className="text-right">Birim Fiyat</TableHead>
                        <TableHead className="text-right">Toplam</TableHead>
                        <TableHead>Tedarikçi</TableHead>
                        <TableHead>Durum</TableHead>
                        <TableHead className="text-center">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {entries.map((entry) => (
                        <TableRow key={entry.id}>
                          <TableCell className="font-medium">{new Date(entry.date).toLocaleDateString('tr-TR')}</TableCell>
                          <TableCell className="font-medium">{entry.product_name}</TableCell>
                          <TableCell>{getCategoryBadge(entry.category)}</TableCell>
                          <TableCell><Badge variant="outline">{productTypeLabels[entry.product_type]}</Badge></TableCell>
                          <TableCell className="text-right">{entry.quantity} {entry.unit}</TableCell>
                          <TableCell className="text-right">{formatCurrency(entry.unit_price)}</TableCell>
                          <TableCell className="text-right font-bold text-red-600">{formatCurrency(entry.total_amount)}</TableCell>
                          <TableCell className="text-muted-foreground">{entry.supplier || '-'}</TableCell>
                          <TableCell>
                            {entry.processing_status === 'processed' ? (
                              <Badge className="bg-green-100 text-green-800">İşlendi</Badge>
                            ) : entry.processing_status === 'pending' ? (
                              <Badge className="bg-amber-100 text-amber-800">Bekliyor</Badge>
                            ) : null}
                          </TableCell>
                          <TableCell>
                            <div className="flex justify-center gap-1">
                              {isManager && entry.product_type === 'karkas' && entry.processing_status !== 'processed' && (
                                <Button variant="ghost" size="sm" onClick={() => openProcessingDialog(entry)} title="Karkas İşle">
                                  <Knife className="w-4 h-4 text-amber-600" />
                                </Button>
                              )}
                              {isManager && (
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(entry)}>
                                  <Pencil className="w-4 h-4" />
                                </Button>
                              )}
                              {isAdmin && (
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(entry.id)} className="text-destructive hover:text-destructive">
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Hazırlık Mutfağı Kuyruğu */}
        {activeTab === 'processing' && (
          <Card data-testid="processing-queue-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <ChefHat className="w-5 h-5 text-amber-600" />
                Hazırlık Mutfağı Kuyruğu
              </CardTitle>
            </CardHeader>
            <CardContent>
              {processingQueue.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <ChefHat size={48} className="mx-auto mb-3 opacity-50" />
                  <p>İşlenmeyi bekleyen karkas ürün yok</p>
                  <p className="text-sm mt-2">Karkas ürün girişi yaptığınızda buraya otomatik eklenir</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Bekleyen Ürünler */}
                  <div>
                    <h4 className="font-medium text-amber-700 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Bekleyen ({processingQueue.filter(p => p.status === 'pending').length})
                    </h4>
                    <div className="space-y-2">
                      {processingQueue.filter(p => p.status === 'pending').map((item) => (
                        <div key={item.id} className="flex items-center justify-between p-4 bg-amber-50 border border-amber-200 rounded-lg">
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-amber-100 rounded-lg">
                              <Beef className="w-6 h-6 text-amber-700" />
                            </div>
                            <div>
                              <p className="font-medium">{item.product_name}</p>
                              <p className="text-sm text-muted-foreground">
                                {item.quantity} {item.unit} | {item.date_received ? new Date(item.date_received).toLocaleDateString('tr-TR') : '-'}
                              </p>
                              {item.supplier && <p className="text-xs text-muted-foreground">Tedarikçi: {item.supplier}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge className="bg-amber-100 text-amber-800">Bekliyor</Badge>
                            {isManager && (
                              <Button size="sm" variant="outline" onClick={() => openProcessingDialog(item)} className="border-amber-300 text-amber-700 hover:bg-amber-100">
                                <Knife className="w-4 h-4 mr-1" />
                                İşle
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                      {processingQueue.filter(p => p.status === 'pending').length === 0 && (
                        <p className="text-muted-foreground text-center py-4">Bekleyen ürün yok</p>
                      )}
                    </div>
                  </div>

                  {/* İşlenmiş Ürünler */}
                  {processingQueue.filter(p => p.status === 'processed').length > 0 && (
                    <div className="mt-6">
                      <h4 className="font-medium text-green-700 mb-3 flex items-center gap-2">
                        <Check className="w-4 h-4" />
                        İşlenmiş ({processingQueue.filter(p => p.status === 'processed').length})
                      </h4>
                      <div className="space-y-2">
                        {processingQueue.filter(p => p.status === 'processed').slice(0, 5).map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                            <div className="flex items-center gap-3">
                              <Check className="w-5 h-5 text-green-600" />
                              <div>
                                <p className="font-medium">{item.product_name}</p>
                                <p className="text-sm text-muted-foreground">
                                  {item.quantity} {item.unit} | {item.date_received ? new Date(item.date_received).toLocaleDateString('tr-TR') : '-'}
                                </p>
                              </div>
                            </div>
                            <Badge className="bg-green-100 text-green-800">İşlendi</Badge>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Raporlar */}
        {activeTab === 'report' && report && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Kategori Bazlı */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Kategori Bazlı Harcama</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {report.by_category.map((cat, idx) => {
                    const percentage = report.total_amount > 0 ? (cat.total_amount / report.total_amount * 100) : 0;
                    return (
                      <div key={idx} className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="font-medium">{getCategoryBadge(cat.category)}</span>
                          <span className="text-muted-foreground">{formatCurrency(cat.total_amount)} (%{percentage.toFixed(1)})</span>
                        </div>
                        <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div className="h-full bg-red-500 rounded-full" style={{ width: `${percentage}%` }} />
                        </div>
                        <p className="text-xs text-muted-foreground">{cat.total_quantity.toFixed(1)} kg, {cat.entry_count} kayıt</p>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>

            {/* Ürün Bazlı */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">En Çok Alınan Ürünler</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.by_product.slice(0, 10).map((prod, idx) => (
                    <div key={idx} className="flex justify-between items-center p-2 bg-slate-50 rounded">
                      <div>
                        <span className="font-medium">{prod.product_name}</span>
                        <span className="text-xs text-muted-foreground ml-2">({prod.total_quantity.toFixed(1)} {prod.unit})</span>
                      </div>
                      <span className="font-bold text-red-600">{formatCurrency(prod.total_amount)}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Karkas İşleme Dialog */}
        <Dialog open={isProcessingDialogOpen} onOpenChange={setIsProcessingDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Knife className="w-5 h-5 text-amber-600" />
                Karkas İşle
              </DialogTitle>
            </DialogHeader>
            {processingEntry && (
              <div className="space-y-4 mt-4">
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="font-medium">{processingEntry.product_name}</p>
                  <p className="text-sm text-amber-700">
                    Giren: {processingEntry.quantity} {processingEntry.unit} | Birim Maliyet: {formatCurrency(processingEntry.unit_price)}
                  </p>
                </div>
                
                <div>
                  <Label className="flex items-center gap-2 mb-2">
                    <Scale className="w-4 h-4" />
                    Çıkan Parçalar
                  </Label>
                  {processingOutputs.map((output, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <Input
                        value={output.name}
                        onChange={(e) => updateOutputRow(idx, 'name', e.target.value)}
                        placeholder="Parça adı (But, Kanat vb.)"
                        className="flex-1"
                      />
                      <Input
                        type="number"
                        step="0.01"
                        value={output.quantity}
                        onChange={(e) => updateOutputRow(idx, 'quantity', e.target.value)}
                        placeholder="Kg"
                        className="w-24"
                      />
                      {processingOutputs.length > 1 && (
                        <Button variant="ghost" size="sm" onClick={() => removeOutputRow(idx)}>
                          <X className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addOutputRow}>
                    <Plus className="w-4 h-4 mr-1" />
                    Parça Ekle
                  </Button>
                </div>
                
                <div>
                  <Label>Fire Miktarı (kg)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={wasteQuantity}
                    onChange={(e) => setWasteQuantity(e.target.value)}
                    placeholder="Kemik, deri vb."
                  />
                </div>
                
                {/* Özet */}
                {(processingOutputs.some(o => o.quantity) || wasteQuantity) && (
                  <div className="p-3 bg-slate-50 border rounded-lg text-sm">
                    <p>Giren: <strong>{processingEntry.quantity} kg</strong></p>
                    <p>Çıkan: <strong>{processingOutputs.reduce((sum, o) => sum + (parseFloat(o.quantity) || 0), 0).toFixed(2)} kg</strong></p>
                    <p>Fire: <strong>{parseFloat(wasteQuantity) || 0} kg</strong> ({((parseFloat(wasteQuantity) || 0) / processingEntry.quantity * 100).toFixed(1)}%)</p>
                  </div>
                )}
                
                <Button onClick={handleProcessCarcass} className="w-full bg-amber-600 hover:bg-amber-700">
                  <Knife className="w-4 h-4 mr-2" />
                  İşle ve Parçalara Ayır
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
